import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Input, IconButton, GoogleMapsLoader } from '../components';
import { BuyerZoneDrawMap } from '../components/BuyerZoneDrawMap';
import { colour } from '../styles/GlobalStyles';
// Explicit, though Buyers.css already arrives incidentally via the components
// barrel on the routes that render this. `.buyer-geo-map-container` is the only
// thing giving the map a height now that BuyerZoneMap.css no longer carries a
// min-height fallback — if that incidental link ever breaks the map collapses
// to 0px rather than degrading, so name the dependency here.
import '../styles/Buyers.css';
import { HugeiconsIcon } from '@hugeicons/react';
import { Delete02Icon } from '@hugeicons/core-free-icons';

// Lazy-load county data only when needed
let countiesModule = null;
const loadCountiesModule = async () => {
    if (!countiesModule) {
        countiesModule = await import('../data/usCounties');
    }
    return countiesModule;
};

function BuyerGeographyModal({ geography, setGeography }) {
    const [activeTab, setActiveTab] = useState('zipCodes');
    const [zipInput, setZipInput] = useState('');
    const [countySearch, setCountySearch] = useState('');
    const [countyResults, setCountyResults] = useState([]);
    const [, setCountySearching] = useState(false);

    const fipsLookupRef = useRef(null);
    const [fipsLoaded, setFipsLoaded] = useState(false);

    // Load FIPS lookup when counties tab is active
    useEffect(() => {
        if (activeTab === 'counties' && !fipsLookupRef.current) {
            loadCountiesModule().then(mod => {
                fipsLookupRef.current = mod.lookupCountyByFips;
                setFipsLoaded(true);
            });
        }
    }, [activeTab]);

    const resolveCountyDisplay = useCallback((value) => {
        if (/^\d+$/.test(value) && fipsLookupRef.current) {
            const county = fipsLookupRef.current(value);
            if (county) return countiesModule ? countiesModule.formatCounty(county) : `${county.name} County, ${county.state}`;
        }
        return value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fipsLoaded]);

    const currentZipCodes = useMemo(() => geography?.zipCodes || [], [geography]);
    const currentCounties = useMemo(() => geography?.counties || [], [geography]);
    const currentPolygons = useMemo(() => geography?.polygons || [], [geography]);

    // ==================== ZIP Codes ====================

    const addZipCodes = useCallback(() => {
        if (!zipInput.trim()) return;

        // Parse comma or space separated ZIP codes
        const newZips = zipInput
            .split(/[,\s]+/)
            .map(z => z.trim())
            .filter(z => /^\d{5}$/.test(z))
            .filter(z => !currentZipCodes.includes(z));

        if (newZips.length > 0) {
            setGeography(prev => ({
                ...prev,
                zipCodes: [...(prev?.zipCodes || []), ...newZips]
            }));
        }
        setZipInput('');
    }, [zipInput, currentZipCodes, setGeography]);

    const removeZipCode = useCallback((zip) => {
        setGeography(prev => ({
            ...prev,
            zipCodes: (prev?.zipCodes || []).filter(z => z !== zip)
        }));
    }, [setGeography]);

    const handleZipKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addZipCodes();
        }
    }, [addZipCodes]);

    // ==================== Counties ====================

    const handleCountySearch = useCallback(async (value) => {
        setCountySearch(value);
        if (!value || value.trim().length < 2) {
            setCountyResults([]);
            return;
        }

        setCountySearching(true);
        try {
            const mod = await loadCountiesModule();
            const searchFn = mod.searchCounties;
            const results = searchFn(value, 20);
            // Filter out already-selected counties
            const filtered = results.filter(c => !currentCounties.includes(c.fips));
            setCountyResults(filtered);
        } catch (error) {
            console.error('Error searching counties:', error);
            setCountyResults([]);
        } finally {
            setCountySearching(false);
        }
    }, [currentCounties]);

    const addCounty = useCallback((county) => {
        setGeography(prev => ({
            ...prev,
            counties: [...(prev?.counties || []), county.fips]
        }));
        setCountySearch('');
        setCountyResults([]);
    }, [setGeography]);

    const removeCounty = useCallback((countyStr) => {
        setGeography(prev => ({
            ...prev,
            counties: (prev?.counties || []).filter(c => c !== countyStr)
        }));
    }, [setGeography]);

    // ==================== Polygons ====================

    const addPolygon = useCallback((polygon) => {
        setGeography(prev => ({
            ...prev,
            polygons: [...(prev?.polygons || []), polygon]
        }));
    }, [setGeography]);

    const removePolygon = useCallback((index) => {
        setGeography(prev => ({
            ...prev,
            polygons: (prev?.polygons || []).filter((_, i) => i !== index)
        }));
    }, [setGeography]);

    const editPolygonGeometry = useCallback((index, geometry) => {
        if (!geometry) return;
        setGeography(prev => ({
            ...prev,
            polygons: (prev?.polygons || []).map((p, i) =>
                i === index ? { ...p, geometry } : p
            )
        }));
    }, [setGeography]);

    return (
        <div className="buyer-geo-modal-container">
            {/* Tabs */}
            <div className="buyer-geo-tabs">
                <div
                    className={`buyer-geo-tab body-semibold ${activeTab === 'zipCodes' ? 'buyer-geo-tab-active colour-primary' : 'colour-secondary'}`}
                    onClick={() => setActiveTab('zipCodes')}
                >
                    ZIP Codes {currentZipCodes.length > 0 && `(${currentZipCodes.length})`}
                </div>
                <div
                    className={`buyer-geo-tab body-semibold ${activeTab === 'counties' ? 'buyer-geo-tab-active colour-primary' : 'colour-secondary'}`}
                    onClick={() => setActiveTab('counties')}
                >
                    Counties {currentCounties.length > 0 && `(${currentCounties.length})`}
                </div>
                <div
                    className={`buyer-geo-tab body-semibold ${activeTab === 'zones' ? 'buyer-geo-tab-active colour-primary' : 'colour-secondary'}`}
                    onClick={() => setActiveTab('zones')}
                >
                    Map Zones {currentPolygons.length > 0 && `(${currentPolygons.length})`}
                </div>
            </div>

            {/* ZIP Codes Section */}
            {activeTab === 'zipCodes' && (
                <div className="buyer-geo-section">
                    <div className="buyer-geo-input-row">
                        <Input
                            label={null}
                            labelType={null}
                            value={zipInput}
                            onChange={(e) => setZipInput(e.target.value)}
                            onKeyDown={handleZipKeyDown}
                            placeholder="Enter ZIP codes (comma separated)..."
                            type="text"
                            leftUnit={null}
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={false}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                        <IconButton
                            size="medium"
                            icon={<span className="body-regular colour-primary">Add</span>}
                            disabled={!zipInput.trim()}
                            clickFunc={addZipCodes}
                            standardIcon={false}
                            background={true}
                            bgType="white"
                            contextTooltip={false}
                        />
                    </div>
                    {currentZipCodes.length > 0 && (
                        <div className="buyer-geo-chips">
                            {currentZipCodes.map(zip => (
                                <div key={zip} className="buyer-geo-chip body-regular colour-primary">
                                    {zip}
                                    <button
                                        className="buyer-geo-chip-remove colour-white"
                                        onClick={() => removeZipCode(zip)}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {currentZipCodes.length === 0 && (
                        <span className="body-regular colour-secondary">
                            No ZIP codes added yet. Type ZIP codes above and press Enter or click Add.
                        </span>
                    )}
                </div>
            )}

            {/* Counties Section */}
            {activeTab === 'counties' && (
                <div className="buyer-geo-section">
                    <div className="buyer-geo-county-search">
                        <Input
                            label={null}
                            labelType={null}
                            value={countySearch}
                            onChange={(e) => handleCountySearch(e.target.value)}
                            placeholder="Search for a county..."
                            type="text"
                            leftUnit={null}
                            rightUnit={null}
                            unitSwitch={false}
                            leftUnitSelected={true}
                            unitSwitchFunc={null}
                            disabled={false}
                            multiline={false}
                            error={false}
                            onMouseEnter={null}
                            onMouseLeave={null}
                            reset={false}
                            resetFunc={null}
                            resetPosition={null}
                            focusMultiline={false}
                            prompt={false}
                            submitPromptDisabled={false}
                            submitPrompt={null}
                            onHeightChange={null}
                        />
                        {countyResults.length > 0 && (
                            <div className="buyer-geo-county-results bg-colour-white">
                                {countyResults.map((county) => (
                                    <div
                                        key={county.fips}
                                        className="buyer-geo-county-result body-regular colour-primary"
                                        onClick={() => addCounty(county)}
                                    >
                                        {countiesModule ? countiesModule.formatCounty(county) : `${county.name} County, ${county.state}`}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {currentCounties.length > 0 && (
                        <div className="buyer-geo-chips">
                            {currentCounties.map(county => (
                                <div key={county} className="buyer-geo-chip body-regular colour-primary">
                                    {resolveCountyDisplay(county)}
                                    <button
                                        className="buyer-geo-chip-remove colour-white"
                                        onClick={() => removeCounty(county)}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {currentCounties.length === 0 && (
                        <span className="body-regular colour-secondary">
                            No counties added yet. Search for a county above to add it.
                        </span>
                    )}
                </div>
            )}

            {/* Map Zones Section */}
            {activeTab === 'zones' && (
                <div className="buyer-geo-section">
                    <div className="buyer-geo-map-container">
                        <GoogleMapsLoader>
                            {(isLoaded) => (
                                <BuyerZoneDrawMap
                                    isMapLoaded={isLoaded}
                                    polygons={currentPolygons}
                                    onPolygonAdd={addPolygon}
                                    onPolygonRemove={removePolygon}
                                    onPolygonEdit={editPolygonGeometry}
                                />
                            )}
                        </GoogleMapsLoader>
                    </div>
                    {currentPolygons.length > 0 ? (
                        <div className="buyer-geo-zone-list">
                            {currentPolygons.map((polygon, index) => (
                                <div key={index} className="buyer-geo-zone-item">
                                    <div className="buyer-geo-zone-item-info">
                                        <span
                                            style={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: polygon.active ? colour.jade02 : colour.gray04,
                                                display: 'inline-block',
                                                flexShrink: 0
                                            }}
                                        />
                                        <span className="body-regular colour-primary">
                                            {polygon.name || `Zone ${index + 1}`}
                                        </span>
                                    </div>
                                    <div className="buyer-geo-zone-actions">
                                        <IconButton
                                            size="small"
                                            icon={<HugeiconsIcon icon={Delete02Icon} color={colour.gray05} size={14} />}
                                            disabled={false}
                                            clickFunc={() => removePolygon(index)}
                                            standardIcon={false}
                                            background={true}
                                            bgType="white"
                                            contextTooltip={false}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span className="body-regular colour-secondary">
                            No map zones defined yet. Click "Draw Zone" on the map to create one.
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default BuyerGeographyModal;
